import { StateCreator, create } from "zustand";
import { PersistOptions, persist } from "zustand/middleware";
import { TaskTabSelected, TaskType } from "./constants";
import { EnrichedTask, WizardId } from "./types";
import { ProfilePayload } from "@earned/wizard";
import _ from "lodash";

interface TaskAndActionsState {
  selectedTask: EnrichedTask | null;
  setSelectedTask: (task: EnrichedTask | null) => void;
  removeTaskModalVisible: boolean;
  setRemoveTaskModalVisible: (isVisible: boolean) => void;
  addNewTaskModalVisible: boolean;
  setAddNewTaskModalVisible: (isVisible: boolean) => void;
  selectedTab: TaskType;
  setSelectedTab: (tab: TaskType) => void;
  newSelectedTab: TaskTabSelected;
  setNewSelectedTab: (tab: TaskTabSelected) => void;
  intakeFormModalVisible: boolean;
  setIntakeFormModalVisible: (isVisible: boolean) => void;
  isWizardOpen: boolean;
  setIsWizardOpen: (open: boolean) => void;
  wizardId: WizardId | null;
  setWizardId: (id: WizardId | null) => void;
  wizardTask: EnrichedTask | null;
  setWizardTask: (task: EnrichedTask | null) => void;
  remoteCompleteTaskId: string | null;
  setRemoteCompleteTaskId: (taskId: string | null) => void;
}

const useTasksStore = create<TaskAndActionsState>((set) => ({
  selectedTask: null,
  setSelectedTask: (task) => set({ selectedTask: task }),
  removeTaskModalVisible: false,
  setRemoveTaskModalVisible: (isVisible: boolean) =>
    set({ removeTaskModalVisible: isVisible }),
  addNewTaskModalVisible: false,
  setAddNewTaskModalVisible: (isVisible: boolean) =>
    set({ addNewTaskModalVisible: isVisible }),
  selectedTab: TaskType.ME,
  setSelectedTab: (tab: TaskType) => set({ selectedTab: tab }),
  newSelectedTab: TaskTabSelected.TASKS,
  setNewSelectedTab: (tab: TaskTabSelected) => set({ newSelectedTab: tab }),
  intakeFormModalVisible: false,
  setIntakeFormModalVisible: (isVisible: boolean) =>
    set({ intakeFormModalVisible: isVisible }),
  isWizardOpen: false,
  setIsWizardOpen: (isOpen) => set({ isWizardOpen: isOpen }),
  wizardId: null,
  setWizardId: (id) => set({ wizardId: id }),
  wizardTask: null,
  setWizardTask: (task) => set({ wizardTask: task }),
  remoteCompleteTaskId: null,
  setRemoteCompleteTaskId: (taskId) =>
    set({
      remoteCompleteTaskId: taskId,
    }),
}));

interface ProfileWizardState {
  defaultPage: number;
  setDefaultPage: (page: number) => void;
  fields: Partial<ProfilePayload>;
  setField: <V>(field: { key: string; value: V }) => void;
  reset: () => void;
}

type ProfileWizardPersist = (
  config: StateCreator<ProfileWizardState>,
  options: PersistOptions<ProfileWizardState>
) => StateCreator<ProfileWizardState>;

const isReactNative =
  typeof navigator !== "undefined" && navigator.product === "ReactNative";

let AsyncStorage: any;

if (isReactNative) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const asyncStorage = require("@react-native-async-storage/async-storage");
  AsyncStorage = asyncStorage.default;
}

const initialState = {
  defaultPage: 0,
  fields: {} as ProfilePayload,
};
const useProfileWizardStore = create<ProfileWizardState>(
  (persist as ProfileWizardPersist)(
    (set) => ({
      ...initialState,
      reset: () => set(initialState),
      setDefaultPage: (page) => set({ defaultPage: page }),
      setField: (field) =>
        set((state) => ({
          ...state,
          fields: _.set(
            { ...state.fields },
            field.key,
            field.value
          ) as ProfilePayload,
        })),
    }),
    {
      name: "profile-wizard-state",
      version: 0,
      getStorage: () => (isReactNative ? AsyncStorage : localStorage),
    }
  )
);

export { useProfileWizardStore, useTasksStore };
export default useTasksStore;
